import { useCallback, useEffect, useRef, useState } from "react";
import { startTransferUpload } from "../services/transferUploader";

/**
 * Upload state for a transfer (selected files, per-file progress, speed / ETA).
 * Used by ShareHome (owner "send") and PublicRequestPage (client uploading to a request link).
 */
export function useTransferUpload() {
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([]); // [{ name, size, status, uploaded, error }]
  const [uploading, setUploading] = useState(false);
  const [speed, setSpeed] = useState(0); // bytes / second
  const cancelRef = useRef(null);
  const samplesRef = useRef([]);

  const addFiles = useCallback((newFiles) => {
    setFiles((prev) => [...prev, ...newFiles]);
    setItems((prev) => [
      ...prev,
      ...newFiles.map((f) => ({ name: f.name, size: f.size, status: "selected", uploaded: 0 })),
    ]);
  }, []);

  const removeFile = useCallback((index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setItems([]);
    setSpeed(0);
  }, []);

  const totalBytes = items.reduce((a, it) => a + it.size, 0);
  const uploadedBytes = items.reduce((a, it) => a + (it.status === "done" ? it.size : it.uploaded || 0), 0);

  const uploadedRef = useRef(0);
  uploadedRef.current = uploadedBytes;

  // Speed over a sliding 5s window
  useEffect(() => {
    if (!uploading) return;
    samplesRef.current = [];
    const id = setInterval(() => {
      const now = Date.now();
      const bytes = uploadedRef.current;
      const samples = [...samplesRef.current, { t: now, bytes }].filter((s) => now - s.t <= 5000);
      samplesRef.current = samples;
      if (samples.length > 1) {
        const first = samples[0];
        setSpeed(Math.max(0, (bytes - first.bytes) / ((now - first.t) / 1000)));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [uploading]);

  // Warn before closing the tab mid-upload
  useEffect(() => {
    if (!uploading) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [uploading]);

  /** Uploads the selected files under `base`. Resolves with { fileIds, bytes } of what was uploaded. */
  const start = useCallback(
    async ({ base, extra }) => {
      setUploading(true);
      setItems((prev) => prev.map((it) => ({ ...it, status: "queued", uploaded: 0, error: null })));
      const { promise, cancel } = startTransferUpload({
        base,
        files,
        extra,
        onFileUpdate: (index, patch) =>
          setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it))),
      });
      cancelRef.current = cancel;
      try {
        return await promise;
      } finally {
        cancelRef.current = null;
        setUploading(false);
      }
    },
    [files]
  );

  const cancel = useCallback(() => cancelRef.current?.(), []);

  const eta = speed > 0 ? Math.ceil((totalBytes - uploadedBytes) / speed) : null;

  return { files, items, addFiles, removeFile, reset, start, cancel, uploading, totalBytes, uploadedBytes, speed, eta };
}
